import { Mail } from "lucide-react";
import { useRef } from "react";
import { confirmCopy, limitsText, UPDATING_LABEL, UPDATING_TOOLTIP } from "../lib/resend";
import { enterConfirms } from "../lib/focusTrap";
import { Alert, Button, Modal } from "./ui";
import { useT } from "../i18n/I18nProvider";

/** "Resend the quote email?": asked before every Resend (components/ProposalActions.tsx). Esc or Cancel closes it,
 * Enter or Resend sends; focus stays inside. It refuses while the proposal is being updated after a design save. */
export function ResendConfirm({ name, email, address, quoteNumber, limits, proposalStatus, sending, onConfirm, onClose }: {
  name?: string | null;
  email?: string | null;
  address?: string | null;
  quoteNumber?: string | null;
  limits?: { per_quote_day: number; per_quote_week: number } | null;
  proposalStatus?: "ready" | "updating" | "failed" | null;
  sending?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const confirmRef = useRef<HTMLButtonElement>(null);
  const copy = confirmCopy({ name, email, address, quoteNumber }, t);
  const updating = proposalStatus === "updating";
  const failed = proposalStatus === "failed";
  const blocked = updating || failed;
  const limit = limitsText(limits, t);
  const send = () => !blocked && !sending && onConfirm();

  return (
    <Modal
      title={copy.title}
      onClose={onClose}
      width={460}
      initialFocus={blocked ? undefined : confirmRef}
      describedBy="resend-confirm-body"
      onKeyDown={(e) => {
        if (e.key === "Enter" && enterConfirms(e.target as HTMLElement, confirmRef.current)) {
          e.preventDefault();
          send();
        }
      }}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            ref={confirmRef}
            variant="primary"
            icon={<Mail />}
            loading={sending || updating}
            disabled={blocked}
            title={updating ? t(UPDATING_TOOLTIP) : undefined}
            onClick={send}
          >
            {updating ? t(UPDATING_LABEL) : t("designs.resend.resend")}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 12 }} id="resend-confirm-body">
        <p style={{ margin: 0 }}>{copy.body}</p>
        {limit && (
          <p className="small muted" style={{ margin: 0 }}>
            {limit}
          </p>
        )}
        {updating && <Alert tone="info">{t("designs.resend.updatingWait")}</Alert>}
        {failed && <Alert tone="danger">{t("designs.resend.updateFailedRetry")}</Alert>}
      </div>
    </Modal>
  );
}
